import styled, { css } from 'styled-components'
import type { ApparelProductProps } from '@velasca/types'
import { PriceRow as UIPriceRow } from '@velasca/ui/price-row'
import { ProductBranding } from '@velasca/ui/product-branding'
import { QuickAddToCartButton } from '@velasca/ui/quick-add-to-cart-button'
import { ResponsiveImage } from '@velasca/ui/responsive-image'

export const ApparelCard = styled.div<{
  $isQuickAdd: boolean
  layout: ApparelProductProps['layout']
}>`
  ${({ layout, theme, $isQuickAdd }) =>
    layout === 'list' ||
    layout === 'condensed-list' ||
    layout === 'ultra-condensed-list'
      ? `
    display: grid;
    grid-template-columns: ${
      layout === 'ultra-condensed-list'
        ? '25.5%'
        : layout === 'condensed-list'
          ? '38.5%'
          : 'minmax(auto, 26.5%)'
    } auto;
    height: fit-content;
  padding: ${layout === 'ultra-condensed-list' ? '0.4rem 0' : '1.2rem 0'};
    border-bottom: 1px solid ${theme.colors.secondary};

    &:first-child {
      padding-top: 0;
    }
  `
      : `
    display: flex;
    flex-direction: column;
    @media screen and (min-width: 768px) {
      &:hover:before {
        content: '';
        position: absolute;
        background-color: ${$isQuickAdd ? theme.colors.background : 'inherit'};
        box-shadow: ${$isQuickAdd ? '0 0.4rem 2.2rem rgba(0, 0, 0, 0.03)' : 'none'};
      }
    }
  `}
  position: relative;
  width: 100%;
`

export const Link = styled.a`
  color: unset !important;

  &:hover,
  &:link,
  &:visited,
  &:active {
    text-decoration: none;
  }
`

export const LinkFlex = styled(Link)<{
  layout: ApparelProductProps['layout']
  hasSole: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: start;
  ${({ layout }) =>
    layout === 'condensed-list' && 'padding-top: 0; padding-bottom: 0;'};
  padding: ${({ layout, hasSole }) =>
    layout === 'ultra-condensed-list'
      ? hasSole
        ? '0.3rem 1rem'
        : '0.5rem 1rem'
      : layout === 'search'
        ? '1rem 0'
        : '1.5rem 1rem'};

  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    outline: none !important;
  }

  &:focus:not(:focus-visible) {
    outline: none !important;
  }

  &:focus h3,
  &:focus-visible h3 {
    outline: 2px solid ${({ theme }) => theme.colors.primary} !important;
    outline-offset: 2px !important;
  }

  &:focus:not(:focus-visible) h3 {
    outline: none !important;
  }
`

export const ProductDetailsWrapper = styled.div<{
  layout: ApparelProductProps['layout']
}>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: ${({ layout }) =>
    layout === 'condensed-list' || layout === 'ultra-condensed-list'
      ? 'column'
      : 'row'};
  flex-grow: 1;
  gap: ${({ layout }) =>
    layout === 'condensed-list' || layout === 'ultra-condensed-list'
      ? '2rem'
      : '1rem'};
  ${({ layout }) =>
    layout !== 'condensed-list' &&
    layout !== 'ultra-condensed-list' &&
    'justify-content: space-between;'};
  align-items: start;

  .apparel-product-price-row {
    margin-top: 0;
  }
`

export const ProductDescriptionsWrapper = styled.div<{
  layout: ApparelProductProps['layout']
}>`
  flex-grow: ${({ layout }) =>
    layout === 'condensed-list' || layout === 'ultra-condensed-list'
      ? '0'
      : '3'};
  display: flex;
  flex-direction: column;
  gap: ${({ layout }) =>
    layout === 'search'
      ? '0.7rem'
      : layout === 'ultra-condensed-list'
        ? '0.5rem'
        : '0.1rem'};
`

// TODO: Here we didn't implemented the `SerifTitle` component beacause we have custom sizes, we should chang design at least for the `dynamic-size` to satisfy the `SerifTitle` component.
export const Title = styled.h3<{ layout: ApparelProductProps['layout'] }>`
  display: block;
  width: 100%;
  font-family: ${({ theme }) => theme.fonts.serif.fontFamily};
  letter-spacing: -1px;
  color: ${({ theme }) => theme.colors.primary};

  > span span {
    font-size: 2.3rem;
  }

  font-size: ${({ layout }) =>
    layout === 'search' || layout === 'ultra-condensed-list'
      ? '2rem'
      : '2.3rem'};
  line-height: ${({ layout }) =>
    layout === 'ultra-condensed-list'
      ? '1'
      : layout === 'search'
        ? '125%'
        : '2.3rem'};
`

export const StyledProductBranding = styled(ProductBranding)``

export const Img = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  object-fit: cover;
  width: 100%;
`

export const HoverImg = styled(Img)`
  display: none;

  @media (hover: hover) and (pointer: fine) {
    display: block;
  }
`

export const AbsoluteQuickAddToCartButton = styled(QuickAddToCartButton)`
  z-index: 1;
  position: absolute;
  bottom: 0.75rem;
  right: 0.25rem;
`

interface ImgWrapperProps {
  visible: boolean
}

const ImgWrapper = styled.div<ImgWrapperProps>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`

export const HoverImgWrapper = styled(ImgWrapper)`
  opacity: 0;
`
export const FrontImgWrapper = styled(ImgWrapper)`
  opacity: 1;
`

export const UpperSection = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;

  > span {
    display: block;
    line-height: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${HoverImgWrapper} {
        opacity: 1;
      }

      ${FrontImgWrapper}:not(.single) {
        opacity: 0;
      }
    }
  }
`

export const Images = styled.div`
  position: relative;
  aspect-ratio: 4/5;

  .safari-lower-than-15 & {
    padding-top: 125%;
  }
`

// export const ProductReviewBadge = styled(ReviewBadge)<{ current: boolean }>`
//   display: ${({ current }) => (current ? 'flex' : 'none')};
//   margin-bottom: 1rem;
//   align-items: baseline;
// `

export const ProductInfo = styled.div<{
  layout: ApparelProductProps['layout']
  isPriceDiscounted: boolean
}>`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 500;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ layout }) => layout === 'card' && 'max-width: 90%;'};
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.light};

  max-width: ${({ isPriceDiscounted }) =>
    isPriceDiscounted ? '150px' : '220px'};

  > span {
    font-size: 1.2rem;
    line-height: 1.8rem;
  }

  font-size: 1.2rem;
  line-height: 1.8rem;

  @media screen and (min-width: 768px) {
    line-height: ${({ layout }) =>
      layout === 'ultra-condensed-list' ? '1' : '1.5rem'};
    font-size: 1.2rem;

    > span span {
      line-height: ${({ layout }) =>
        layout === 'ultra-condensed-list' ? '1' : '1.5rem'};
      font-size: 1.2rem;
    }
  }
`

export const Season = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`

export const SeasonLabel = styled.span`
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-size: 1.4rem;
  line-height: normal;
`

export const Price = styled.span<{ isDiscounted?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 500;
  color: ${({ theme, isDiscounted }) =>
    isDiscounted ? theme.colors.hover : theme.colors.text.primary};
  font-size: 1.8rem !important;
  line-height: 1.8rem !important;
  white-space: nowrap;
  text-align: right;

  ${({ isDiscounted }) =>
    isDiscounted &&
    css`
      text-decoration: line-through;
      text-decoration-thickness: 1px;
      font-weight: 400;
      font-size: 1.6rem !important;
    `}
`

export const Description = styled.div<{
  layout: ApparelProductProps['layout']
}>`

  ${({ layout }) =>
    layout === 'list' ||
    layout === 'condensed-list' ||
    layout === 'ultra-condensed-list'
      ? `
      display: flex;
      flex-direction: column;
    `
      : `
      display: inline-block;
    `}
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 2rem;

  @media screen and (min-width: 768px) {
    line-height: 2rem;
  }

  span {
    font-family: ${({ theme }) => theme.fonts.brand};
    font-size: 1.4rem;
    line-height: inherit;

    ${({ layout }) =>
      (layout === 'list' ||
        layout === 'condensed-list' ||
        layout === 'ultra-condensed-list') &&
      `

      &.separator {
        display: none;
      }
    `}
`

export const DescriptionBold = styled.span`
  font-weight: 500;
`

export const DescriptionThin = styled.span`
  font-weight: 400;
`

export const DescriptionBlock = styled(DescriptionThin)`
  display: block;
  width: 100%;
`

export const SlideUp = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`

export const ActionsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 0.5rem;
`

export const StyledPriceRow = styled(UIPriceRow)<{
  layout: ApparelProductProps['layout']
}>`
  ${({ layout }) =>
    layout === 'ultra-condensed-list'
      ? `
    position: absolute;
    top: 0;
    right: 0;
  `
      : ''}
`
